import React, { useEffect, useMemo, useState } from 'react';
import { Box, Preloader } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { Topbar } from '@/modules/core/components';
import { useBreadcrumbs, useHistory } from '@/modules/core/hooks';
import { LockboxStatus } from '@/modules/employer/components/Lockbox/lockbox.types';
import {
  LockboxDetailsHeader,
  LockboxDocumentPreview,
  LockboxErrorModal,
  LockboxInfo,
} from '@/modules/employer/components/Lockbox/LockboxDetails/components';
import LockboxInfoFooter from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxInfoFooter/LockboxInfoFooter';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useLockboxDividingModeStore } from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDividingMode.store';
import useGetLockboxQuery from '@/modules/employer/components/Lockbox/queries/useGetLockbox.query';

interface Props {
  lockboxId: string;
}

const LockboxDetails: React.FC<Props> = ({ lockboxId }) => {
  const history = useHistory();
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const {
    formattedData: lockbox,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useGetLockboxQuery({ lockboxId });

  const isUpdated = useLockboxDetailsStore(state => state.editableLockbox?.isUpdated);
  const setEditableLockbox = useLockboxDetailsStore(state => state.setEditableLockbox);
  const setOriginalLockbox = useLockboxDetailsStore(state => state.setOriginalLockbox);
  const setIsEditMode = useLockboxDetailsStore(state => state.setIsEditMode);
  const reset = useLockboxDetailsStore(state => state.reset);
  const resetDividingStore = useLockboxDividingModeStore(state => state.reset);

  const lockboxName = lockbox?.name || '';

  const isLockboxInEditMode = useMemo(() => lockbox.lockboxStatus === LockboxStatus.NEW
    || lockbox.lockboxStatus === LockboxStatus.PENDING
    || lockbox.lockboxStatus === LockboxStatus.INVESTMENTS_LIQUIDATED, [lockbox]);

  useBreadcrumbs([
    {
      route: ROUTES.FINOPS,
      title: 'FinOps',
    },
    {
      route: ROUTES.FINOPS_LOCKBOXES,
      title: 'Lockbox',
    },
    {
      title: lockboxName,
    }
  ], [lockboxName]);

  // Clear filter params
  useEffect(() => {
    return () => {
      if (window.location.pathname !== ROUTES.FINOPS_LOCKBOXES) {
        history.replace({
          pathname: window.location.pathname,
          search: '',
          hash: window.location.hash,
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const isNeedToUpdateEditableLockbox = !isUpdated && !isFetching;
  useEffect(() => {
    if (isNeedToUpdateEditableLockbox) {
      const employee = lockbox?.matchEmployees?.find((matchedEmployee) => matchedEmployee?.employeeId === lockbox?.employeeId);
      setEditableLockbox({
        isUpdated: true,
        lockboxId: lockbox?.id,
        lockboxType: lockbox?.lockboxType,
        amount: lockbox?.amount || lockbox?.ocr?.amount,
        accountId: lockbox?.accountId,
        employeeId: lockbox?.employeeId,
        employeeAccountStatus: employee?.accountStatus,
        employeeInvestmentsBalance: employee?.investmentsBalance,
        employeeAvailableAmount: employee?.availableAmount,
        lockboxStatus: lockbox?.lockboxStatus,
        vendorId: lockbox?.checkDetails?.vendorId,
        liquidateAllFunds: lockbox?.liquidateAllFunds,
        isMultipleRecordsWarning: lockbox?.isMultipleRecordsWarning,
        suggestedClaims: lockbox?.suggestedClaims,
        claimRepayments: lockbox?.claimRepayments,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNeedToUpdateEditableLockbox]);

  useEffect(() => {
    if (!isFetching) {
      setIsEditMode(isLockboxInEditMode);
      setOriginalLockbox(lockbox);
      resetDividingStore();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  useEffect(() => {
    return () => {
      reset();
      resetDividingStore();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isError) {
      setIsErrorModalVisible(true);
    }
  }, [isError]);

  if (isErrorModalVisible) {
    return <LockboxErrorModal onSubmit={refetch} onSetVisible={setIsErrorModalVisible} />;
  }

  return (
    <Box height="inherit">
      <Topbar isFull />
      <Box height="inherit">
        <LockboxDetailsHeader
          lockboxId={lockboxId}
          lockboxName={lockboxName}
          lockboxStatus={lockbox?.lockboxStatus}
        />
        {
          isLoading ? (
            <Preloader testId="lockbox-details-preloader" />
          ) : (
            <Box
              data-testid="lockbox-details-content"
              direction="row"
              background="module"
              height="inherit"
            >
              <Box basis="1/2">
                <LockboxDocumentPreview documentId={lockbox?.documentId} />
              </Box>
              <Box
                basis="1/2"
                overflow={{ vertical: 'auto' }}
                data-testid="lockbox-details-form-wrapper"
              >
                <Box height={{ min: 'auto' }}>
                  <LockboxInfo />
                </Box>
                <LockboxInfoFooter />
              </Box>
            </Box>
          )
        }
      </Box>
    </Box>
  );
};

export default LockboxDetails;
